/* The alert detail pop-up: what the alert says and EVERY result it covers (the same rows as
 * the email's results table). Opening it marks the alert read; Acknowledge (Alt+A) is offered
 * while the alert still needs one. Shared AppDialog + AppDataGrid (standing rules 7/9). */
import { useEffect, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useAcknowledgeAlert } from './useAcknowledgeAlert';
import { formatCombo } from '../../../components/hotkeys';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { getAlert, type InAppAlert, type InAppAlertDetail } from './inboxApi';
import { useInboxStore } from './inboxStore';
import { useAlertPopupSettings, needsAck } from './alertPopupSettings';
import { SeverityChip, AckChip } from './AlertChips';

const COLUMNS: AppColumn<InAppAlertDetail>[] = [
  { field: 'jobId', header: 'Job' },
  { field: 'sampleId', header: 'Sample' },
  { field: 'parameter', header: 'Parameter' },
  { field: 'result', header: 'Result' },
  { field: 'units', header: 'Units' },
  { field: 'limit', header: 'Permit limit' },
  { field: 'batch', header: 'Batch' },
  { field: 'permit', header: 'Permit' },
];

export function AlertDetailDialog() {
  const detailId = useInboxStore((s) => s.detailId);
  const close = useInboxStore((s) => s.closeDetail);
  const settings = useAlertPopupSettings();
  const acknowledgeAlert = useAcknowledgeAlert();
  const notify = useNotify();
  const [alert, setAlert] = useState<InAppAlert | null>(null);

  useEffect(() => {
    setAlert(null);
    if (!detailId) return;
    let cancelled = false;
    getAlert(detailId)
      .then((a) => { if (!cancelled) setAlert(a); })
      .catch(() => { if (!cancelled) { notify.error('Could not open the alert. It may have been removed.'); close(); } });
    return () => { cancelled = true; };
  }, [detailId, close, notify]);

  const canAck = !!alert && needsAck(alert, settings);
  const ack = async () => {
    if (!alert || !canAck) return;
    if (await acknowledgeAlert(alert.id)) setAlert({ ...alert, ackAt: new Date().toISOString() });
  };

  return (
    <AppDialog
      open={!!detailId}
      title={alert?.subject ?? 'Alert'}
      icon="ti-bell-ringing"
      width={900}
      maximizable
      submitOnEnter={false}
      onClose={close}
      hotkeys={[{ combo: 'alt+a', run: () => void ack(), enabled: canAck }]}
      footer={
        <>
          {canAck && (
            <button type="button" className="ca-btn ca-btn-solid" onClick={() => void ack()}
              title={`Acknowledge (${formatCombo('alt+a')})`}>
              <i className="ti ti-circle-check" /> Acknowledge
            </button>
          )}
          <button type="button" className="ca-btn" onClick={close} title="Close (Esc)">Close</button>
        </>
      }
    >
      {!alert ? (
        <div className="ia-empty">Loading…</div>
      ) : (
        <>
          <div className="ia-detail-meta">
            <SeverityChip severity={alert.severity} />
            <AckChip required={needsAck({ ackRequired: alert.ackRequired, ackAt: null, popup: alert.popup }, settings)} ackAt={alert.ackAt} resolvedAt={alert.resolvedAt} />
            <span>Rule <b>{alert.ruleName}</b></span>
            {alert.triggerLabel && <span>Raised by <b>{alert.triggerLabel}</b></span>}
            <span>At <b>{fmtLocalDateTime(alert.createdAt)}</b></span>
            {alert.ackAt && <span>Acknowledged <b>{fmtLocalDateTime(alert.ackAt)}</b></span>}
            {alert.resolvedAt && <span>Resolved <b>{fmtLocalDateTime(alert.resolvedAt)}</b></span>}
          </div>
          {alert.summary && <div className="ia-detail-sum">{alert.summary}</div>}
          <AppDataGrid
            rows={alert.details}
            columns={COLUMNS}
            getRowId={(_, i) => i}
            maximizable
            exportFilename={`alert-${alert.ruleCode}`}
            emptyText="No result details were recorded for this alert."
          />
        </>
      )}
    </AppDialog>
  );
}
